import { useEffect, useRef, useCallback, useMemo } from "react"
import styled from "styled-components"

// 型定義
interface Circle {
  id: number
  color: string
  baseSize: number
  angle: number
  rotationSpeed: number
  verticalOffset: number
  verticalFactor: number
  phase: number
  oscillationAmplitude: number
  oscillationSpeed: number
  oscillationPhaseOffset: number
  x?: number
  y?: number
  size?: number
  opacity?: number
  depthFactor?: number
}

// Styled components
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
  background-color: white;
  border-radius: 8px;
`

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  border-radius: 8px;
`

const FlowingCircles = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const circlesRef = useRef<Circle[]>([])
  const mousePosRef = useRef<{ x: number; y: number }>({ x: -100, y: -100 })
  const isMouseOverRef = useRef<boolean>(false)
  const lastFrameTimeRef = useRef<number>(0)
  const frameCountRef = useRef<number>(0)

  // Fixed parameters with user-specified values
  const depthFactor = 1.5
  const speedFactor = 1.5
  const sizeFactor = 1.2
  const extendedRange = 0.2 // 画面外の余分な範囲

  // より多くの円を生成し、縦方向のバリエーションを増加
  const segmentCount = 10 
  const circlesPerSegment = 4 // 3から4に増加

  // 垂直レーン数を5に増加
  const verticalLaneCount = 5

  // Original 5 colors
  const colors = useMemo(() => [
    "#FFE100", // Bright yellow
    "#0066CC", // Deep blue
    "#00B4D8", // Light cyan/turquoise
    "#FF6B35", // Orange/coral
    "#E71D36", // Red
  ], [])

  // 色バランス選択の事前計算テーブル
  const colorSelectionTableRef = useRef<Record<number, string>>({})
  
  // 色の使用回数をカウントする
  const colorCountRef = useRef<{ [color: string]: number }>({})
  
  // 最適化された色選択 - 全体のバランスを考慮
  const selectBalancedColor = useCallback((existingCircles: Circle[], newAngle: number): string => {
    // 色の使用回数を初期化（初回のみ）
    if (Object.keys(colorCountRef.current).length === 0) {
      colors.forEach(color => {
        colorCountRef.current[color] = 0
      })
    }
    
    // 使用頻度の低い色を優先して選択する
    // 20%の確率で完全にバランスを優先（使用回数最小の色を選択）
    if (Math.random() < 0.2) {
      let minCount = Infinity
      let leastUsedColors: string[] = []
      
      // 最も使用回数の少ない色を見つける
      colors.forEach(color => {
        const count = colorCountRef.current[color] || 0
        if (count < minCount) {
          minCount = count
          leastUsedColors = [color]
        } else if (count === minCount) {
          leastUsedColors.push(color)
        }
      })
      
      // 同じ使用回数の色が複数ある場合はランダムに選択
      const selectedColor = leastUsedColors[Math.floor(Math.random() * leastUsedColors.length)]
      
      // 選択した色の使用回数をインクリメント
      colorCountRef.current[selectedColor] = (colorCountRef.current[selectedColor] || 0) + 1
      
      return selectedColor
    }
    
    // 80%の確率で角度に基づいた選択と使用頻度を組み合わせる
    // 角度を5度単位に丸めてキーとして使用
    const key = Math.floor(newAngle * 36 / Math.PI) % 36
    
    // 既存の円の色のうち、近い角度にある色の出現頻度を集計
    const nearbyColors: { [color: string]: number } = {}
    const angleThreshold = 0.2
    
    existingCircles.forEach(circle => {
      if (Math.abs(circle.angle - newAngle) < angleThreshold) {
        if (!nearbyColors[circle.color]) {
          nearbyColors[circle.color] = 0
        }
        nearbyColors[circle.color]++
      }
    })
    
    // 近くに少ない色 + グローバルに少ない色を選択
    let bestColors: string[] = []
    let bestScore = -Infinity
    
    colors.forEach(color => {
      // 近くでの使用回数（存在しない場合は0）
      const nearbyCount = nearbyColors[color] || 0
      // グローバルな使用回数
      const globalCount = colorCountRef.current[color] || 0
      
      // スコア計算 - 使用回数が少ないほど高スコア
      // 近くの使用回数により重みを置く
      const score = -(nearbyCount * 2 + globalCount * 1)
      
      if (score > bestScore) {
        bestScore = score
        bestColors = [color]
      } else if (score === bestScore) {
        bestColors.push(color)
      }
    })
    
    // 最適な色をランダムに選択
    const selectedColor = bestColors[Math.floor(Math.random() * bestColors.length)]
    
    // 選択した色の使用回数をインクリメント
    colorCountRef.current[selectedColor] = (colorCountRef.current[selectedColor] || 0) + 1
    
    // ルックアップテーブルを更新（将来の参照用）
    colorSelectionTableRef.current[key] = selectedColor
    
    return selectedColor
  }, [colors])

  // スロットリングを適用したマウスムーブハンドラー
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      if (e.clientX >= rect.left && e.clientX <= rect.right && 
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
        isMouseOverRef.current = true
        mousePosRef.current = {
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        }
      } else {
        isMouseOverRef.current = false
      }
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    isMouseOverRef.current = true
  }, [])

  const handleMouseLeave = useCallback(() => {
    isMouseOverRef.current = false
  }, [])

  // キャンバスサイズの更新
  const updateCanvasSize = useCallback(() => {
    if (canvasRef.current && containerRef.current) {
      const canvas = canvasRef.current
      const container = containerRef.current
      const rect = container.getBoundingClientRect()
      
      // デバイスピクセル比を考慮した高解像度キャンバス
      const dpr = window.devicePixelRatio || 1
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null
      if (ctx) ctx.scale(dpr, dpr)
    }
  }, [])

  // ランダムな垂直レーンを選択する関数
  const selectVerticalLane = useCallback((): number => {
    // より広い範囲の垂直レーン：-2, -1, 0, 1, 2
    const laneIndex = Math.floor(Math.random() * verticalLaneCount)
    return (laneIndex - Math.floor(verticalLaneCount/2)) / (Math.floor(verticalLaneCount/2))
  }, [verticalLaneCount])

  // 初期化とアニメーション
  useEffect(() => {
    updateCanvasSize()
    
    // 初期円の生成
    const initialCircles = []
    
    for (let segment = 0; segment < segmentCount; segment++) {
      const baseAngle = (segment / segmentCount) * Math.PI
      
      for (let i = 0; i < circlesPerSegment; i++) {
        const angleVariation = (i / circlesPerSegment - 0.5) * 0.8 * (Math.PI / segmentCount)
        const angle = baseAngle + angleVariation
        
        const color = selectBalancedColor(initialCircles, angle)
        
        // 縦方向のバリエーションを大幅に強化
        const verticalFactor = selectVerticalLane()
        
        // 垂直方向の配置を画面の70%の範囲に広げる
        const verticalBaseOffset = verticalFactor * 100
        
        // さらに細かいランダム性を追加
        const verticalRandomOffset = (Math.random() * 40 - 20)
        
        initialCircles.push({
          id: segment * circlesPerSegment + i,
          color,
          baseSize: 16 + Math.floor(Math.random() * (140 - 16)),
          angle,
          rotationSpeed: 0.002 + Math.random() * 0.005, // さらに遅くして観察しやすく
          verticalOffset: verticalBaseOffset + verticalRandomOffset,
          verticalFactor, // 垂直位置係数を保存
          phase: Math.random() * Math.PI * 2,
          oscillationAmplitude: 10 + Math.random() * 15, // 振動の振幅を大きく
          oscillationSpeed: 0.01 + Math.random() * 0.02, // 若干遅く
          oscillationPhaseOffset: Math.random() * Math.PI * 2, // 位相オフセットを追加
        })
      }
    }
    
    circlesRef.current = initialCircles
    
    // メイン描画関数
    const draw = (timestamp: number) => {
      frameCountRef.current++
      
      // 30FPSにスロットリング（約33.3ms）
      if (timestamp - lastFrameTimeRef.current < 33.3) {
        animationRef.current = requestAnimationFrame(draw)
        return
      }
      
      lastFrameTimeRef.current = timestamp
      
      const canvas = canvasRef.current
      if (!canvas) return
      
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null
      if (!ctx) return
      const width = canvas.width / (window.devicePixelRatio || 1)
      const height = canvas.height / (window.devicePixelRatio || 1)
      
      // キャンバスをクリア
      ctx.clearRect(0, 0, width, height)
      
      // 深さでソートして奥から順に描画
      const sortedCircles = [...circlesRef.current].sort((a, b) => {
        const depthA = a.depthFactor !== undefined ? a.depthFactor : Math.abs(Math.cos(a.angle)) || 0
        const depthB = b.depthFactor !== undefined ? b.depthFactor : Math.abs(Math.cos(b.angle)) || 0
        return depthA - depthB
      })
      
      // 円の更新と描画
      const updatedCircles = []
      const mousePos = mousePosRef.current
      const isMouseOver = isMouseOverRef.current
      const time = timestamp * 0.001 // 秒単位の時間
      
      for (const circle of sortedCircles) {
        const adjustedRotationSpeed = circle.rotationSpeed * speedFactor
        let newAngle = circle.angle + adjustedRotationSpeed
        
        // 画面外まで含めた拡張された範囲で処理
        if (newAngle > Math.PI * (1 + extendedRange)) {
          // 円が拡張された範囲の終わりに達した場合、開始位置にリセット
          newAngle = -Math.PI * extendedRange
          const newColor = selectBalancedColor(circlesRef.current, newAngle)
          
          // 新しい垂直位置を決定
          const newVerticalFactor = selectVerticalLane()
          const newVerticalBaseOffset = newVerticalFactor * 70
          const newVerticalRandomOffset = (Math.random() * 40 - 20)
          
          updatedCircles.push({
            ...circle,
            angle: newAngle,
            baseSize: 16 + Math.floor(Math.random() * (140 - 16)),
            color: newColor,
            verticalOffset: newVerticalBaseOffset + newVerticalRandomOffset,
            verticalFactor: newVerticalFactor,
            phase: Math.random() * Math.PI * 2,
            oscillationAmplitude: 10 + Math.random() * 15,
            oscillationSpeed: 0.01 + Math.random() * 0.02,
            oscillationPhaseOffset: Math.random() * Math.PI * 2,
          })
        } else {
          // 位置と大きさの計算
          const normalizedAngle = (newAngle / Math.PI)
          const x = normalizedAngle * width
          
          // 深さファクター計算
          const rawDepthFactor = Math.abs(Math.cos(newAngle)) || 0
          const adjustedDepthFactor = Math.pow(rawDepthFactor, 1.5) * depthFactor
          
          // 垂直位置計算 - 収束効果を大幅に減少
          const convergenceFactor = Math.max(0, 1 - adjustedDepthFactor) * 0.3 // 収束効果を70%減少
          const baseY = height / 2
          
          // 垂直オフセットをほぼそのまま適用
          const scaledOffset = circle.verticalOffset * (1 - convergenceFactor)
          
          // より複雑な振動パターン
          const oscillation1 = circle.oscillationAmplitude * 
                               Math.sin(newAngle * 2 + circle.phase + time * circle.oscillationSpeed)
          
          // 二次的な振動を追加
          const oscillation2 = (circle.oscillationAmplitude * 0.5) * 
                               Math.sin(newAngle * 3 + circle.oscillationPhaseOffset + time * circle.oscillationSpeed * 1.5)
          
          // 複合的な振動
          const oscillation = oscillation1 + oscillation2
                             
          const y = baseY + scaledOffset + oscillation
          
          // サイズ計算
          const sizeScale = (0.4 + 0.6 * adjustedDepthFactor) * sizeFactor
          const size = Math.max(1, circle.baseSize * sizeScale)
          
          let attractedX = x
          let attractedY = y
          
          // マウスによる引力
          if (isMouseOver) {
            const distX = x - mousePos.x * width
            const distY = y - mousePos.y * height
            const distance = Math.sqrt(distX * distX + distY * distY)
            
            const attractionRadius = 20 // 少し大きく
            const maxAttractionStrength = 0.3
            
            if (distance < attractionRadius) {
              const falloff = Math.pow(1 - distance / attractionRadius, 2)
              const attractionStrength = maxAttractionStrength * falloff * adjustedDepthFactor
              
              attractedX = x - distX * attractionStrength
              attractedY = y - distY * attractionStrength
            }
          }
          
          updatedCircles.push({
            ...circle,
            angle: newAngle,
            x: attractedX,
            y: attractedY,
            size,
            opacity: 1.0,
            depthFactor: adjustedDepthFactor,
          })
          
          // multiply効果の適用
          ctx.globalCompositeOperation = 'multiply'
          
          // 円を描画
          ctx.beginPath()
          ctx.arc(attractedX, attractedY, size / 2, 0, Math.PI * 2)
          ctx.fillStyle = circle.color
          ctx.fill()
        }
      }
      
      // 描画完了後にコンポジションモードをリセット
      ctx.globalCompositeOperation = 'source-over'
      
      circlesRef.current = updatedCircles
      animationRef.current = requestAnimationFrame(draw)
    }
    
    // アニメーション開始
    animationRef.current = requestAnimationFrame(draw)
    
    // リサイズイベントリスナー
    const handleResize = () => {
      updateCanvasSize()
    }
    
    window.addEventListener('resize', handleResize)
    document.addEventListener('mousemove', handleMouseMove)
    
    // クリーンアップ
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [selectBalancedColor, updateCanvasSize, handleMouseMove, depthFactor, speedFactor, sizeFactor, selectVerticalLane, extendedRange])

  return (
    <Container ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Canvas ref={canvasRef} />
    </Container>
  )
}

export default FlowingCircles
